'use client';

import { useEffect, useMemo } from 'react';
import Script from 'next/script';
import { Provider as ReduxProvider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import store, { persistor } from './redux/store';
import { usePathname, useRouter } from 'next/navigation';
import theme from '../theme/theme';
import { LanguageProvider, useLanguage } from '../hooks';
import AuthHydrator from './AppProviders';
import { BookingProvider } from './bookingContext';
import { GlobalNotificationToast } from '../kit';
import { NotificationProvider } from '@/context/NotificationContext';
import { ErrorBoundary } from '../components';
import SessionExpiredToast from '../components/SessionExpiredToast';
import InstallPWAPrompt from '../components/common/InstallPWAPrompt';
import { NetworkStatusProvider } from '../components/FailureHandling/useErrorHandler';
import RegisterParcelDevice from '../components/RegisterParcelDevice';
import { brandingConfig } from '@/config/branding';

// Create QueryClient outside component to avoid recreation on re-renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Client-side providers wrapper component
 * Handles Redux, React Query, Theme, and other client-specific providers
 */
export default function ClientProviders({ children }) {
  const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

  return (
    <ReduxProvider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          {/* Load Google Maps API client-side using Next Script */}
          {GOOGLE_MAPS_API_KEY && (
            <Script
              src={`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry,geocoding`}
              strategy="afterInteractive"
            />
          )}

          <ThemeProvider theme={theme}>
            <CssBaseline />
            <LanguageProvider>
              <NotificationProvider>
                <ErrorBoundary>
                  <NetworkStatusProvider>
                    <AuthHydrator>
                      <BookingProvider>
                        <LayoutContent>{children}</LayoutContent>
                      </BookingProvider>
                    </AuthHydrator>
                  </NetworkStatusProvider>
                  <GlobalNotificationToast />
                  <SessionExpiredToast />
                  <InstallPWAPrompt />
                  <RegisterParcelDevice />
                </ErrorBoundary>
              </NotificationProvider>
            </LanguageProvider>
          </ThemeProvider>
          {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </PersistGate>
    </ReduxProvider>
  );
}

/**
 * Layout content with authentication routing logic
 */
function LayoutContent({ children }) {
  const pathname = usePathname();
  const router = useRouter();
  const { language } = useLanguage();

  // Dynamic page titles
  const pageTitle = useMemo(() => {
    const path = pathname?.replace('/', '') || 'home';
    return brandingConfig.pageTitles[path] || brandingConfig.pageTitles.default;
  }, [pathname]);

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  // Service Worker management - only in production
  useEffect(() => {
    const isDev = process.env.NODE_ENV === 'development';

    if ('serviceWorker' in navigator) {
      if (isDev) {
        // In development: Unregister service workers
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((registration) => {
            registration.unregister();
          });
        });
      } else {
        // In production: Register service worker (delayed to not block initial render)
        const registerSW = () => {
          navigator.serviceWorker
            .register('/sw.js')
            .then((registration) => {
              // Check for updates every 60 seconds
              setInterval(() => registration.update(), 60000);
            })
            .catch((error) => {
              console.error('Service Worker registration failed:', error);
            });
        };

        // Delay SW registration until after first paint
        if (document.readyState === 'complete') {
          setTimeout(registerSW, 1000);
        } else {
          window.addEventListener('load', () => setTimeout(registerSW, 1000));
        }
      }
    }
  }, []);

  // Telugu font loading
  useEffect(() => {
    if (language === 'te') {
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Suranna&display=swap';
      link.rel = 'stylesheet';
      link.id = 'suranna-font';
      document.head.appendChild(link);
      document.body.style.setProperty('--projectL-font', "'Suranna', Mulish, sans-serif");
    } else {
      const suranna = document.getElementById('suranna-font');
      if (suranna) suranna.remove();
      document.body.style.setProperty('--projectL-font', "'Mulish', sans-serif");
    }
  }, [language]);

  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const isHydrated = useSelector((state) => state.auth.isHydrated);
  const userType = useSelector((state) => state.auth.userType);
  const businessSetupCompleted = useSelector((state) => state.auth.businessSetupCompleted);

  // Only allow these public routes for unauthenticated users
  const publicRoutes = useMemo(
    () => [
      '/landing',
      '/login',
      '/register',
      '/',
      '/estimate',
      '/terms',
      '/support',
      '/about',
      '/privacy',
      '/pricing',
    ],
    []
  );
  const isPublic = useMemo(() => {
    if (!pathname) return false;
    // Check if pathname starts with any public route
    return publicRoutes.some((route) => pathname === route || pathname.startsWith(route + '/'));
  }, [pathname, publicRoutes]);

  // Handle authentication and routing logic
  useEffect(() => {
    if (!isHydrated) return; // Wait for hydration

    // Redirect to login if not logged in and trying to access protected route
    if (!isLoggedIn && !isPublic) {
      router.replace('/login');
      return;
    }

    // Redirect logged-in users away from public login pages to their dashboard
    if (
      isLoggedIn &&
      isHydrated &&
      (pathname === '/login' || pathname === '/landing' || pathname === '/')
    ) {
      if (!userType) {
        router.replace('/user-type');
      } else if (String(userType).toLowerCase() === 'business') {
        router.replace(businessSetupCompleted ? '/business-dashboard' : '/business-setup');
      } else {
        router.replace('/home');
      }
      return;
    }

    // Business user routing logic
    if (isLoggedIn && isHydrated) {
      // If user hasn't selected type yet, redirect to user-type page
      if (!userType && pathname !== '/user-type') {
        router.replace('/user-type');
        return;
      }

      // If business user hasn't completed setup, redirect to business-setup
      if (
        userType &&
        String(userType).toLowerCase() === 'business' &&
        !businessSetupCompleted &&
        pathname !== '/business-setup' &&
        pathname !== '/user-type'
      ) {
        router.replace('/business-setup');
        return;
      }

      // If business user completed setup but on home page, redirect to business dashboard
      if (
        userType &&
        String(userType).toLowerCase() === 'business' &&
        businessSetupCompleted &&
        pathname === '/home'
      ) {
        router.replace('/business-dashboard');
        return;
      }

      // If individual user trying to access business pages, redirect to home
      if (
        userType &&
        String(userType).toLowerCase() === 'individual' &&
        (pathname === '/business-dashboard' || pathname === '/business-setup')
      ) {
        router.replace('/home');
        return;
      }
    }
  }, [isHydrated, isLoggedIn, isPublic, pathname, userType, businessSetupCompleted, router]);

  // Recover from chunk loading failures (e.g. stale CDN cache after deploy)
  useEffect(() => {
    const handleChunkError = (event) => {
      const err = event?.error || event;
      const message = err?.message || '';
      const isChunkError =
        (err && err.name === 'ChunkLoadError') || /Loading chunk .* failed/.test(message);
      if (!isChunkError) return;

      console.warn('[ChunkHandler] Detected chunk load failure:', err);

      // Prevent reload loops: allow one reload every 5 minutes per session
      try {
        const key = 'ofc_chunk_reload_ts';
        const last = Number(sessionStorage.getItem(key) || 0);
        const now = Date.now();
        if (!last || now - last > 5 * 60 * 1000) {
          sessionStorage.setItem(key, String(now));
          // Hard reload to fetch latest static assets
          window.location.reload();
        } else {
          console.warn('[ChunkHandler] Reload suppressed to avoid loop');
        }
      } catch (e) {
        // If sessionStorage is unavailable, still attempt a reload
        window.location.reload();
      }
    };

    const handleRejection = (ev) => {
      const reason = ev?.reason;
      if (reason && /Loading chunk .* failed/.test(reason.message || '')) {
        handleChunkError({ error: reason });
      }
    };

    window.addEventListener('error', handleChunkError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleChunkError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  // Direct render - Next.js handles routing natively
  return <ErrorBoundary>{children}</ErrorBoundary>;
}
